import { HTTPMethod } from '../../endpoints/httpMethods';
import { generateQueryParams } from '../generateQueryParams';

export type FetchRequestArgs = RequestInit & {
  endpoint?: string;
  domain?: string;
  data?: Record<string, unknown>;
  query?: Record<string, string | number | boolean>;
};

export type FetchRequestReturn<T> = {
  body: {
    data: T;
  };
  error?: {
    message: string;
    response: unknown;
  };
  statusCode: number;
};

/**
 * This is a simple wrapper around a standard `fetch` request.
 * It provides defaults for the http method and content type,
 * and standardizes the response object and request URL.
 *
 * It also uses a `data` argument for the body to standardize the type
 * expected and how it is transformed to fit the content type.
 *
 * Type parameters are added for convenience in defining the expected
 * return response and the allowed domains.
 *
 * @docs https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API
 *
 * @todo Should endpoints be documented in an enum here or provided at all?
 */
export const fetchRequest = async <T>({
  data = {},
  domain,
  endpoint,
  headers,
  method,
  query = {},
  signal = null,
}: FetchRequestArgs & {
  method?: keyof typeof HTTPMethod;
}): Promise<FetchRequestReturn<T>> => {
  const requestURL = domain
    ? `https://${domain}.usehatchapp.com/api${endpoint}${generateQueryParams(
        query,
      )}`
    : `/api${endpoint}${generateQueryParams(query)}`;

  const requestMethod = method || HTTPMethod.GET;
  const requestOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    method: requestMethod,
    signal,
  };

  if (requestMethod !== HTTPMethod.GET) {
    if (typeof data === 'object') {
      requestOptions.body = JSON.stringify(data);
    } else {
      requestOptions.body = data;
    }
  }

  return (
    fetch(requestURL, requestOptions)
      .then(async (response) => {
        const { status: statusCode } = response;
        const contentType = response.headers.get('content-type');
        const responseData =
          contentType && contentType.indexOf('application/json') !== -1
            ? await response.json()
            : null;

        if (statusCode >= 500) {
          return {
            body: {
              data: null,
            },
            error: {
              message: 'Internal server error',
              response: responseData?.errors ?? 'Internal server error',
            },
            statusCode,
          };
        }

        if (responseData) {
          if (typeof responseData === 'object') {
            if (statusCode >= 400) {
              return {
                body: {
                  data: null,
                },
                error: {
                  message: Array.isArray(responseData.errors)
                    ? responseData.errors[0]
                    : JSON.stringify(responseData),
                  response: responseData,
                },
                statusCode,
              };
            }

            // Object types with the data key are spread onto the body property
            if (responseData) {
              if ('data' in responseData) {
                return { body: { ...responseData }, statusCode };
              }

              // Non-array like objects without a data key are spread into a data property for consistency
              if (!Array.isArray(responseData)) {
                return { body: { data: { ...responseData } }, statusCode };
              }
            }
          }

          // Primitives and arrays are directly assigned the the `data` property
          return { body: { data: responseData }, statusCode };
        }

        // Empty 200 and 204 responses
        return { body: { data: responseData }, statusCode };
      })
      .then((parsedResponse) => {
        return { statusCode: 200, ...parsedResponse };
      })
      // Standardize the error response on the FE
      // @todo this should be expected from the BE at some point
      .catch((err) => {
        return {
          body: {
            data: null,
          },
          error: {
            message: err.message,
            response: err.response,
          },
          statusCode: err.status,
        };
      })
  );
};
